import { PendingReturnItem } from "@redotech/redo-return-core-common/draft-return/draft-return-items";
import {
  ReturnOption,
  returnOptionJsonFormat,
} from "@redotech/redo-return-core-common/return-flow";
import { ReturnOptionMethod } from "@redotech/redo-return-core-common/return-flow/return-option";
import { CompensationType } from "./draft-return";

export const isExchangeOption = (option: ReturnOption) =>
  option.method.type === ReturnOptionMethod.EXCHANGE;

export const isAdvancedExchangeOption = (option: ReturnOption) =>
  isExchangeOption(option) && option.exchangeTypes.advanced;

const isSameItemExchangeOption = (option: ReturnOption) =>
  isExchangeOption(option) && option.exchangeTypes.sameItem;

export const isStoreCreditOption = (option: ReturnOption) =>
  option.method.type === ReturnOptionMethod.STORE_CREDIT;

export const isRefundOption = (option: ReturnOption) =>
  option.method.type === ReturnOptionMethod.REFUND;

export const isRepairOption = (option: ReturnOption) =>
  option.method.type === ReturnOptionMethod.REPAIR;

/**
 * Filters exchange options for TNPL contracts to only allow same item exchanges
 */
const getExchangeFilterForTnpl = (hasTnplContract: boolean) =>
  hasTnplContract ? isSameItemExchangeOption : isExchangeOption;

/**
 * Gets the filter function for a specific compensation type.
 * Using Record types ensures all CompensationType values must be handled.
 */
export const getCompensationTypeOptionFilter = (
  type: CompensationType,
  hasTnplContract: boolean,
): ((option: ReturnOption) => boolean) => {
  const filters: Record<CompensationType, (option: ReturnOption) => boolean> = {
    exchange: getExchangeFilterForTnpl(hasTnplContract),
    storeCredit: isStoreCreditOption,
    refund: isRefundOption,
    repair: isRepairOption,
  };
  return filters[type];
};

/**
 * Gets fallback filter functions for a specific compensation type.
 * Using Record types ensures all CompensationType values must be handled.
 */
const getFallbackFiltersForType = (
  type: CompensationType,
  hasTnplContract: boolean,
): ((option: ReturnOption) => boolean)[] => {
  const exchangeFilter = getExchangeFilterForTnpl(hasTnplContract);
  const fallbackFilters: Record<
    CompensationType,
    ((option: ReturnOption) => boolean)[]
  > = {
    exchange: [exchangeFilter, isStoreCreditOption, isRefundOption],
    storeCredit: [isStoreCreditOption, isRefundOption, exchangeFilter],
    refund: [isRefundOption, isStoreCreditOption, exchangeFilter],
    repair: [isRepairOption],
  };
  return fallbackFilters[type];
};

/**
 * The option a compensation method of this type builds an item from: the first
 * option matching the type, or the first one matching a fallback the type
 * accepts. Undefined when the item offers nothing the type can compensate.
 */
export const getReturnOptionForCompensationType = (
  options: readonly ReturnOption[],
  type: CompensationType,
  hasTnplContract: boolean,
): ReturnOption | undefined => {
  for (const filter of getFallbackFiltersForType(type, hasTnplContract)) {
    const option = options.find(filter);
    if (option) {
      return option;
    }
  }
  return undefined;
};

/** The item fields a compensation method reads its return options from. */
export interface CompensationOptionItem {
  returnOptions?: PendingReturnItem["returnOptions"] | null;
  selectedReturnOption?: PendingReturnItem["selectedReturnOption"];
  metadata?: PendingReturnItem["metadata"];
}

/**
 * The options a compensation method may build an item from: the option the
 * shopper picked, plus the advanced exchange a Shop Now bonus keeps in play;
 * every option while nothing is picked yet.
 */
export function getCompensationReturnOptions(
  pendingReturnItem: CompensationOptionItem,
): ReturnOption[] {
  const allOptions = () =>
    (pendingReturnItem.returnOptions ?? []).map(returnOptionJsonFormat.read);
  const selectedOption = pendingReturnItem.selectedReturnOption
    ? returnOptionJsonFormat.read(pendingReturnItem.selectedReturnOption)
    : undefined;
  if (!selectedOption) {
    return allOptions();
  }
  if (!selectedOption.shopNowBonusEligible) {
    return [selectedOption];
  }
  if (pendingReturnItem.metadata?.temporary) {
    return allOptions();
  }
  const exchangeOption = allOptions().find(isAdvancedExchangeOption);
  return exchangeOption ? [selectedOption, exchangeOption] : [selectedOption];
}
